import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import {
  BILLING_PRODUCTS,
  compareBillingFeature,
} from "@polifonia/interlude/billingConstants";
import type { BillingFeatureItem } from "@polifonia/interlude/models";
import type { muiTheme } from "@polifonia/theme";
import { groupBy } from "es-toolkit/array";
import { mapValues } from "es-toolkit/object";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { useInactiveAiAgentFeatures } from "@/features/billing/useInactiveAiAgentFeatures";
import { interlude } from "@/interlude";
import { FeaturePricingCard } from "@/pages/billing/v2/tabs/featurePricing/FeaturePricingCard";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    paddingTop: "48px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "32px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const FeaturePricingTab: React.FC = function FeaturePricingTab() {
  const { t } = useTranslation();
  const featuresQuery = interlude.billing.useGetFeatures();
  const inactiveFeatures = useInactiveAiAgentFeatures();

  const groupedFeatures = useMemo((): Record<
    string,
    Array<BillingFeatureItem>
  > => {
    if (!featuresQuery.isSuccess) return {};
    const grouped = groupBy(
      featuresQuery.data.filter((f) => !inactiveFeatures.has(f.feature)),
      (f) => f.product,
    );
    return mapValues(grouped, (features) =>
      features.toSorted((a, b) => compareBillingFeature(a.feature, b.feature)),
    );
  }, [featuresQuery.isSuccess, featuresQuery.data, inactiveFeatures]);

  return (
    <Box sx={styles.root}>
      {featuresQuery.isPending ? (
        <Stack alignItems="center" py="48px">
          <CircularProgress />
        </Stack>
      ) : featuresQuery.isError ? (
        <Stack alignItems="center" py="48px">
          <Typography variant="body">{t("billing.error.generic")}</Typography>
        </Stack>
      ) : (
        <Box sx={styles.grid}>
          {BILLING_PRODUCTS.map((product) => {
            const features = groupedFeatures[product];
            if (!features || features.length === 0) return null;
            return (
              <FeaturePricingCard
                key={product}
                product={product}
                features={features}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
